/**
 * MinistrareIA - Auth Service
 * Authentication related API calls
 * 
 * UPDATED: 
 * - Removed manual infinitagents_api_key from registration (now automatic)
 * - Added InfinitAgents integration status and retry methods
 * - Compatible with onboarding flow
 */
import api from './api';

const authService = {
  /**
   * Register a new user
   * 
   * InfinitAgents integration is now automatic:
   * - Backend creates InfinitAgents account using user's email
   * - Backend generates API key named "InfinitAgents-WhatsApp"
   * - If integration fails, it can be retried via retryInfinitAgentsIntegration()
   * 
   * Note: After registration, user needs to complete onboarding
   * 
   * @param {string} email - User's email
   * @param {string} password - User's password  
   * @param {string} name - User's display name (optional)
   * @returns {Promise<Object>} Response with user data and token
   */
  async register(email, password, name = null) {
    // NOTE: infinitagents_api_key is no longer sent
    // It's automatically generated by the backend
    const response = await api.post('/api/users/register', {
      email,
      password,
      name,
    });
    return response.data;
  },

  /**
   * Login user
   * Response includes onboarding_completed, default_channel, and infinitagents_status
   */
  async login(email, password) {
    const response = await api.post('/api/users/login', {
      email,
      password,
    });
    return response.data;
  },

  /**
   * Get current user profile
   * Includes onboarding_completed, default_channel, and infinitagents_status
   */
  async getProfile() {
    const response = await api.get('/api/users/me');
    return response.data;
  },

  /**
   * Get stored token
   */
  getToken() {
    return localStorage.getItem('token');
  },

  /**
   * Update user profile
   */
  async updateProfile(data) {
    const response = await api.put('/api/users/me', data);
    return response.data;
  },

  /**
   * Get user labels
   */
  async getLabels() {
    const response = await api.get('/api/users/me/labels');
    return response.data;
  },

  /**
   * Create label
   */
  async createLabel(name, color) {
    const response = await api.post('/api/users/me/labels', { name, color });
    return response.data;
  },

  /**
   * Delete label
   */
  async deleteLabel(labelId) {
    const response = await api.delete(`/api/users/me/labels/${labelId}`);
    return response.data;
  },

  // ============================================
  // 🆕 INFINITAGENTS INTEGRATION
  // ============================================

  /**
   * Get InfinitAgents integration status.
   * 
   * Returns:
   * - status: "success" | "pending" | "failed" | "not_attempted"
   * - has_api_key: boolean
   * - error: string | null
   * - needs_retry: boolean
   * 
   * @returns {Promise<Object>} Integration status
   */
  async getInfinitAgentsStatus() {
    const response = await api.get('/api/users/me/infinitagents/status');
    return response.data;
  },

  /**
   * Retry InfinitAgents integration.
   * 
   * Call this when:
   * - User accesses a feature requiring InfinitAgents
   * - infinitagents_status is "pending" or "failed"
   * 
   * @returns {Promise<Object>} Retry result with updated user data
   */
  async retryInfinitAgentsIntegration() {
    const response = await api.post('/api/users/me/infinitagents/retry');
    return response.data;
  },

  /**
   * Check if InfinitAgents integration needs retry and perform it.
   * 
   * This is a convenience method that checks status and retries if needed.
   * Returns true if integration is successful (either already or after retry).
   * 
   * @returns {Promise<boolean>} True if integration is successful
   */
  async ensureInfinitAgentsIntegration() {
    try {
      const statusResponse = await this.getInfinitAgentsStatus();
      const status = statusResponse.data;

      // Already successful
      if (status.status === 'success' && status.has_api_key) {
        return true;
      }

      // Needs retry
      if (status.needs_retry) {
        const retryResponse = await this.retryInfinitAgentsIntegration();
        return retryResponse.data?.infinitagents_status === 'success';
      }

      return false;
    } catch (error) {
      console.error('Error ensuring InfinitAgents integration:', error);
      return false;
    }
  },

  /**
   * Get user's agents from InfinitAgents
   * Requires infinitagents_api_key to be configured
   */
  async getAgents() {
    const response = await api.get('/agents');
    return response.data;
  },

  /**
   * Set active agent for autopilot
   * @param {string|null} agentId - Agent ID or null to disable
   */
  async setActiveAgent(agentId) {
    const response = await api.put('/api/users/me/active-agent', {
      agent_id: agentId,
    });
    return response.data;
  },

  // ============================================
  // 🆕 AUTOPILOT CONFIGURATION
  // ============================================

  /**
   * Get autopilot configuration.
   * Returns autopilot_mode, autopilot_contacts, active_agent_id
   */
  async getAutopilotConfig() {
    const response = await api.get('/api/users/me/autopilot-config');
    return response.data;
  },

  /**
   * Update autopilot configuration.
   * @param {string} mode - "all" | "specific" | "off"
   * @param {string[]} contacts - Phone numbers for "specific" mode
   */
  async updateAutopilotConfig(mode, contacts = null) {
    const response = await api.put('/api/users/me/autopilot-config', {
      autopilot_mode: mode,
      autopilot_contacts: contacts,
    });
    return response.data;
  },

  /**
   * Toggle autopilot for a specific contact.
   * @param {string} phone - Phone number
   * @param {boolean} enabled - True to add, false to remove
   */
  async toggleAutopilotContact(phone, enabled) {
    const response = await api.put('/api/users/me/autopilot-contact', {
      phone,
      enabled,
    });
    return response.data;
  },

  /**
   * Toggle blocked status for a specific contact.
   * @param {string} phone - Phone number
   * @param {boolean} blocked - True to block, false to unblock
   */
  async toggleBlockedContact(phone, blocked) {
    const response = await api.put('/api/users/me/blocked-contact', {
      phone,
      blocked,
    });
    return response.data;
  },

  // ============================================
  // 🆕 ONBOARDING
  // ============================================

  /**
   * Get onboarding status.
   * 
   * Returns:
   * - onboarding_completed: boolean
   * - default_channel: string | null
   * - infinitagents_status: string
   * - has_api_key: boolean
   * - redirect: string (suggested redirect path)
   * 
   * @returns {Promise<Object>} Onboarding status
   */
  async getOnboardingStatus() {
    const response = await api.get('/api/users/me/onboarding-status');
    return response.data;
  },

  /**
   * Select channel during onboarding.
   * @param {string} channel - 'wa_personal' or 'wa_business'
   * @returns {Promise<Object>} Response with redirect path
   */
  async selectChannel(channel) {
    const response = await api.post('/api/users/me/select-channel', { channel });
    return response.data;
  },

  // ============================================
  // SESSION MANAGEMENT
  // ============================================

  /**
   * Logout - clear local storage
   */
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  /**
   * Get stored user
   */
  getStoredUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Store auth data
   */
  storeAuthData(user, token) {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
  },

  /**
   * Update stored user data
   * @param {Object} userData - Updated user data
   */
  updateStoredUser(userData) {
    const currentUser = this.getStoredUser();
    const updatedUser = { ...currentUser, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    return updatedUser;
  },
};

export default authService;